// P3-021: Particle effects for match-3 battle
// Match destroy particle burst, combo screen flash

import Phaser from 'phaser';
import { ElementType } from '@/shared/types/common';

/** Element color mapping for particles */
const ELEMENT_PARTICLE_COLORS: Record<ElementType, number> = {
  [ElementType.RED]: 0xe05545,
  [ElementType.BLUE]: 0x5890e0,
  [ElementType.GREEN]: 0x52c07a,
  [ElementType.YELLOW]: 0xe6b832,
  [ElementType.PURPLE]: 0x9b6ad8,
};

/**
 * Generate a texture key for particle of given color.
 * Creates it on-the-fly if not cached.
 */
function ensureParticleTexture(scene: Phaser.Scene, color: number): string {
  const key = `particle_${color.toString(16)}`;
  if (scene.textures.exists(key)) return key;

  const canvas = document.createElement('canvas');
  canvas.width = 8;
  canvas.height = 8;
  const ctx = canvas.getContext('2d');
  if (ctx) {
    const r = (color >> 16) & 0xff;
    const g = (color >> 8) & 0xff;
    const b = color & 0xff;
    ctx.fillStyle = `rgb(${r}, ${g}, ${b})`;
    ctx.beginPath();
    ctx.arc(4, 4, 4, 0, Math.PI * 2);
    ctx.fill();
  }
  scene.textures.addCanvas(key, canvas);
  return key;
}

/**
 * Spawn a particle burst at the given position for a matched tile.
 * Particles match the element color.
 */
export function spawnMatchBurst(
  scene: Phaser.Scene,
  worldX: number,
  worldY: number,
  element: ElementType,
): void {
  const color = ELEMENT_PARTICLE_COLORS[element] ?? 0xffffff;
  const textureKey = ensureParticleTexture(scene, color);

  const emitter = scene.add.particles(worldX, worldY, textureKey, {
    speed: { min: 50, max: 150 },
    angle: { min: 0, max: 360 },
    scale: { start: 1.2, end: 0 },
    alpha: { start: 1, end: 0 },
    lifespan: 400,
    quantity: 8,
    emitting: false,
  });

  emitter.setDepth(90);
  emitter.explode(8);

  // Auto-destroy after particles expire
  scene.time.delayedCall(600, () => {
    emitter.destroy();
  });
}

/**
 * Spawn an impact burst effect at the enemy position when projectile hits.
 * Larger, more dramatic burst than the match burst.
 */
export function spawnImpactBurst(
  scene: Phaser.Scene,
  worldX: number,
  worldY: number,
  color: number,
): void {
  const textureKey = ensureParticleTexture(scene, color);

  // Main impact burst
  const emitter = scene.add.particles(worldX, worldY, textureKey, {
    speed: { min: 80, max: 200 },
    angle: { min: 0, max: 360 },
    scale: { start: 1.5, end: 0 },
    alpha: { start: 1, end: 0 },
    lifespan: 500,
    quantity: 12,
    emitting: false,
  });

  emitter.setDepth(92);
  emitter.explode(12);

  // Ring effect
  const ring = scene.add.graphics();
  ring.setDepth(91);
  ring.lineStyle(3, color, 0.8);
  ring.strokeCircle(worldX, worldY, 10);

  scene.tweens.add({
    targets: ring,
    scaleX: 3,
    scaleY: 3,
    alpha: 0,
    duration: 300,
    ease: 'Sine.easeOut',
    onComplete: () => {
      ring.destroy();
    },
  });

  // Auto-destroy particles
  scene.time.delayedCall(700, () => {
    emitter.destroy();
  });
}

/**
 * Spawn a glow ring around a matched tile during the highlight phase.
 * Ring expands outward and fades — signals "this tile is about to be destroyed."
 */
export function spawnMatchGlowRing(
  scene: Phaser.Scene,
  worldX: number,
  worldY: number,
  color: number,
  duration: number,
): void {
  const ring = scene.add.graphics();
  ring.setDepth(88);
  ring.lineStyle(2, color, 0.9);
  ring.strokeCircle(0, 0, 20);
  ring.setPosition(worldX, worldY);

  scene.tweens.add({
    targets: ring,
    scaleX: 2.2,
    scaleY: 2.2,
    alpha: 0,
    duration,
    ease: 'Sine.easeOut',
    onComplete: () => {
      ring.destroy();
    },
  });
}

/**
 * Spawn a combo screen flash effect.
 * Briefly flashes the screen border with a glowing effect.
 */
export function spawnComboFlash(
  scene: Phaser.Scene,
  comboCount: number,
): void {
  if (comboCount < 2) return;

  const { width, height } = scene.scale;
  const intensity = Math.min(0.5, 0.15 + comboCount * 0.08);

  const flash = scene.add.graphics();
  flash.setDepth(95);

  // Draw border glow
  const borderSize = 4 + comboCount;
  flash.fillStyle(0xe6a640, intensity);
  // Top
  flash.fillRect(0, 0, width, borderSize);
  // Bottom
  flash.fillRect(0, height - borderSize, width, borderSize);
  // Left
  flash.fillRect(0, 0, borderSize, height);
  // Right
  flash.fillRect(width - borderSize, 0, borderSize, height);

  scene.tweens.add({
    targets: flash,
    alpha: 0,
    duration: 300,
    ease: 'Sine.easeOut',
    onComplete: () => {
      flash.destroy();
    },
  });
}
